import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X, Sparkles, Zap, Crown, Loader2 } from 'lucide-react';
import { getPlans, createCheckout, changePlan } from '../../services/billingService';
import { useAuth } from '../../stores/AuthContext';
import { useSubscription } from '../../hooks/useSubscription';
import {
  trackPricingViewed, trackBillingToggled, trackPlanClicked,
  trackCheckoutStarted, trackPlanChanged,
} from '../../services/analytics';

const PLAN_ICONS = {
  free: null,
  basic: Zap,
  pro: Sparkles,
  elite: Crown,
};

const PLAN_ACCENT = {
  free: 'border-border',
  basic: 'border-blue-500/40',
  pro: 'border-primary ring-2 ring-primary/20',
  elite: 'border-amber-500/40',
};

const PLAN_CTA_STYLE = {
  free: 'bg-white/5 text-muted-foreground border border-border hover:bg-white/10',
  basic: 'bg-blue-600 text-white hover:bg-blue-700',
  pro: 'bg-primary text-primary-foreground hover:bg-primary/90',
  elite: 'bg-amber-600 text-white hover:bg-amber-700',
};

// Display-friendly feature descriptions
// ai_coach_access is omitted — it's an internal gate; Sage Messages is the user-facing limit
const FEATURE_DISPLAY = {
  dashboard_access:           { label: 'Pulse Dashboard',           category: 'Core' },
  performance_access:         { label: 'Edge Analytics',            category: 'Core' },
  trade_replay_access:        { label: 'Trade Replay',              category: 'Core' },
  export_reports:             { label: 'Export Reports',            category: 'Core' },
  screenshot_uploads:         { label: 'Trade Screenshots',         category: 'Core', unit: '/trade' },
  screenshot_ai_analysis:     { label: 'Screenshot AI Analysis',   category: 'Core' },
  journals_per_week:          { label: 'Chronicle Sessions',        category: 'Journaling', unit: '/week' },
  ai_messages_per_month:      { label: 'Sage AI Messages',          category: 'AI', unit: '/month' },
  psychology_premium_widgets: { label: 'Cortex Premium Insights',   category: 'Analytics' },
  custom_rules:               { label: 'Blueprint Custom Rules',    category: 'Blueprint' },
  playbook_analytics:         { label: 'Blueprint Analytics',       category: 'Blueprint' },
};

// Screenshot upload limits per plan (mirrored from ScreenshotUpload.js)
const SCREENSHOT_LIMITS = { free: 1, basic: 1, pro: 3, elite: 5 };

function formatLimit(feature) {
  if (feature.limit_type === 'boolean') {
    return feature.limit_value === 1;
  }
  if (feature.limit_type === 'unlimited') {
    return 'Unlimited';
  }
  return feature.limit_value;
}

export default function PricingPage() {
  const [plans, setPlans] = useState([]);
  const [isYearly, setIsYearly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [affiliateCode, setAffiliateCode] = useState('');
  const [discountCode, setDiscountCode] = useState('');
  const [codeError, setCodeError] = useState(null);
  const { user } = useAuth();
  const { planId: currentPlanId, billingInterval: currentInterval, periodEnd } = useSubscription();
  const navigate = useNavigate();
  const [resultMessage, setResultMessage] = useState(null);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const data = await getPlans();
        setPlans(data.plans || []);
      } catch (error) {
        console.error('Error fetching plans:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  useEffect(() => {
    trackPricingViewed({
      source: document.referrer.includes('/pricing') ? 'direct' : 'direct',
      current_plan: currentPlanId,
      billing_toggle: isYearly ? 'yearly' : 'monthly',
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Determine if selecting a plan would be an upgrade or downgrade
  const getChangeType = (targetPlanId) => {
    const planOrder = ['free', 'basic', 'pro', 'elite'];
    const currentIdx = planOrder.indexOf(currentPlanId);
    const targetIdx = planOrder.indexOf(targetPlanId);
    const targetInterval = isYearly ? 'yearly' : 'monthly';

    // Same interval, higher tier
    if (currentInterval === targetInterval && targetIdx > currentIdx) return 'upgrade';
    // Monthly → Yearly with same or higher tier
    if (currentInterval === 'monthly' && targetInterval === 'yearly' && targetIdx >= currentIdx) return 'upgrade';
    // Yearly → Yearly with higher tier
    if (currentInterval === 'yearly' && targetInterval === 'yearly' && targetIdx > currentIdx) return 'upgrade';

    return 'downgrade';
  };

  const handleSubscribe = async (planId) => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (planId === 'free') return;

    const interval = isYearly ? 'yearly' : 'monthly';
    const changeType = getChangeType(planId);

    // Validate codes if present
    if ((affiliateCode || discountCode) && changeType === 'downgrade') {
      setCodeError('Discount codes cannot be applied to downgrades');
      return;
    }

    // For downgrades, confirm with the user
    if (currentPlanId !== 'free' && changeType === 'downgrade') {
      const endDate = periodEnd ? new Date(periodEnd).toLocaleDateString() : 'the end of your billing period';
      if (!window.confirm(
        `Your current plan will remain active until ${endDate}, then switch to the new plan. Continue?`
      )) {
        return;
      }
    }

    trackPlanClicked({ plan_id: planId, interval, action_type: changeType, current_plan: currentPlanId });
    setCheckoutLoading(planId);
    setResultMessage(null);
    setCodeError(null);
    try {
      // If user already has a paid plan, use changePlan (upgrade/downgrade)
      if (currentPlanId !== 'free') {
        const result = await changePlan(planId, interval, affiliateCode, discountCode);

        // Checkout fallback (shouldn't happen for paid users, but just in case)
        if (result.url) {
          window.location.href = result.url;
          return;
        }

        if (result.action === 'upgraded') {
          trackPlanChanged({ from_plan: currentPlanId, to_plan: planId, interval, action: 'upgraded' });
          setResultMessage({
            type: 'success',
            text: result.discount_applied_cents
              ? `${result.message} (Discount applied: $${(result.discount_applied_cents / 100).toFixed(2)})`
              : result.message
          });
          setTimeout(() => navigate('/billing'), 2000);
          return;
        }

        if (result.action === 'scheduled') {
          trackPlanChanged({ from_plan: currentPlanId, to_plan: planId, interval, action: 'scheduled' });
          setResultMessage({ type: 'info', text: result.message });
          setTimeout(() => navigate('/billing'), 2500);
          return;
        }

        if (result.action === 'no_change') {
          setResultMessage({ type: 'info', text: result.message });
          setCheckoutLoading(null);
          return;
        }
      } else {
        // Free user — go through Stripe Checkout
        trackCheckoutStarted({ plan_id: planId, interval });
        const { url } = await createCheckout(planId, interval, affiliateCode, discountCode);
        if (url) {
          window.location.href = url;
        }
      }
    } catch (error) {
      console.error('Plan change error:', error);
      setResultMessage({ type: 'error', text: error.response?.data?.error || 'Something went wrong. Please try again.' });
      setCheckoutLoading(null);
    }
  };

  const getCtaText = (planId) => {
    if (!user) return 'Get Started';
    if (planId === currentPlanId && (isYearly ? 'yearly' : 'monthly') === currentInterval) return 'Current Plan';
    if (planId === 'free') return 'Free';

    if (currentPlanId === 'free') return 'Upgrade';

    const changeType = getChangeType(planId);
    return changeType === 'upgrade' ? 'Upgrade' : 'Downgrade';
  };

  // Collect all unique feature keys across plans
  const allFeatureKeys = plans.length > 0
    ? Object.keys(plans[0]?.features || {})
    : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-4 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
            Choose Your Plan
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Start free and upgrade as you grow. All plans include core trading journal features.
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-3 mt-8">
            <span className={`text-sm ${!isYearly ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
              Monthly
            </span>
            <button
              onClick={() => {
                const newIsYearly = !isYearly;
                setIsYearly(newIsYearly);
                trackBillingToggled({ interval: newIsYearly ? 'yearly' : 'monthly' });
              }}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                isYearly ? 'bg-primary' : 'bg-white/10'
              }`}
            >
              <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                isYearly ? 'translate-x-6' : ''
              }`} />
            </button>
            <span className={`text-sm ${isYearly ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
              Yearly
            </span>
            {isYearly && (
              <span className="text-xs font-medium text-green-400 bg-green-400/10 px-2 py-0.5 rounded-full">
                Save ~17%
              </span>
            )}
          </div>
        </div>

        {/* Pricing Note */}
        <p className="text-center text-xs text-muted-foreground mt-4 mb-8">
          All prices in USD. Excludes applicable taxes.
        </p>

        {/* Discount/Affiliate Code Section */}
        <div className="max-w-lg mx-auto mb-8 p-4 rounded-lg bg-white/5 border border-border">
          <h3 className="text-sm font-semibold text-foreground mb-3">Have a code?</h3>
          <div className="space-y-2">
            <input
              type="text"
              placeholder="Affiliate code"
              value={affiliateCode}
              onChange={(e) => {
                setAffiliateCode(e.target.value.toUpperCase());
                setCodeError(null);
              }}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm placeholder-muted-foreground focus:outline-none focus:border-primary/50"
            />
            <input
              type="text"
              placeholder="Discount code"
              value={discountCode}
              onChange={(e) => {
                setDiscountCode(e.target.value.toUpperCase());
                setCodeError(null);
              }}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm placeholder-muted-foreground focus:outline-none focus:border-primary/50"
            />
            {codeError && (
              <p className="text-xs text-red-400">{codeError}</p>
            )}
            <p className="text-xs text-muted-foreground">Discounts apply to upgrades only. Enter during checkout.</p>
          </div>
        </div>

        {/* Result Message */}
        {resultMessage && (
          <div className={`max-w-lg mx-auto mb-8 p-4 rounded-lg text-sm text-center ${
            resultMessage.type === 'success' ? 'bg-green-500/10 text-green-400 border border-green-500/20' :
            resultMessage.type === 'error' ? 'bg-red-500/10 text-red-400 border border-red-500/20' :
            'bg-blue-500/10 text-blue-400 border border-blue-500/20'
          }`}>
            {resultMessage.text}
          </div>
        )}

        {/* Plan Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan) => {
            const Icon = PLAN_ICONS[plan.id];
            const isCurrent = plan.id === currentPlanId;
            const price = isYearly ? plan.yearly_price_cents : plan.monthly_price_cents;
            const monthlyPrice = isYearly
              ? (plan.yearly_price_cents )
              : plan.monthly_price_cents;

            return (
              <div
                key={plan.id}
                className={`relative flex flex-col bg-card rounded-xl border-2 ${PLAN_ACCENT[plan.id]} p-6 ${
                  plan.id === 'pro' ? 'shadow-lg shadow-primary/10' : ''
                }`}
              >
                {plan.id === 'pro' && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-xs font-semibold bg-primary text-primary-foreground rounded-full">
                    Most Popular
                  </div>
                )}

                {/* Plan Header */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    {Icon && <Icon className="w-5 h-5 text-primary" />}
                    <h3 className="text-lg font-semibold text-foreground">{plan.name}</h3>
                  </div>
                  <p className="text-xs text-muted-foreground">{plan.description}</p>
                </div>

                {/* Price */}
                <div className="mb-6">
                  {price === 0 ? (
                    <div className="text-3xl font-bold text-foreground">Free</div>
                  ) : (
                    <>
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold text-foreground">
                          ${(monthlyPrice / 100).toFixed(2)}
                        </span>
                        <span className="text-sm text-muted-foreground">/mo</span>
                      </div>
                      {isYearly && (
                        <p className="text-xs text-muted-foreground mt-1">
                          ${(price / 100).toFixed(2)} billed yearly
                        </p>
                      )}
                    </>
                  )}
                </div>

                {/* Features */}
                <ul className="space-y-2.5 mb-6 flex-1">
                  {allFeatureKeys.map((key) => {
                    // Skip internal gate — represented by ai_messages_per_month instead
                    if (key === 'ai_coach_access') return null;
                    const feature = plan.features[key];
                    if (!feature) return null;
                    const display = FEATURE_DISPLAY[key];
                    // Skip features not in the display map (internal-only keys)
                    if (!display) return null;

                    // For Sage Messages: if ai_coach_access is false (free tier), show as unavailable
                    let value = formatLimit(feature);
                    if (key === 'ai_messages_per_month') {
                      const coachAccess = plan.features['ai_coach_access'];
                      if (coachAccess && formatLimit(coachAccess) === false) {
                        value = false;
                      }
                    }
                    // For screenshot uploads: show per-trade count instead of boolean
                    if (key === 'screenshot_uploads') {
                      value = SCREENSHOT_LIMITS[plan.id] ?? 1;
                    }

                    return (
                      <li key={key} className="flex items-start gap-2.5 text-sm">
                        {value === false ? (
                          <X className="w-4 h-4 text-muted-foreground/40 mt-0.5 flex-shrink-0" />
                        ) : (
                          <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                        )}
                        <span className={value === false ? 'text-muted-foreground/40' : 'text-muted-foreground'}>
                          {typeof value === 'string' || typeof value === 'number' ? (
                            <>
                              <span className="text-foreground font-medium">
                                {value}
                              </span>{' '}
                              {display.label}{display.unit || ''}
                            </>
                          ) : (
                            display.label
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>

                {/* CTA Button */}
                <button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={(isCurrent && (isYearly ? 'yearly' : 'monthly') === currentInterval) || checkoutLoading === plan.id || plan.id === 'free'}
                  className={`w-full py-2.5 px-4 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    PLAN_CTA_STYLE[plan.id]
                  }`}
                >
                  {checkoutLoading === plan.id ? (
                    <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                  ) : (
                    getCtaText(plan.id)
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {/* Financial Disclaimer */}
        <div className="mt-12 pt-8 border-t border-border text-center">
          <p className="text-xs text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            CTX is an educational trading journal tool for self-reflection purposes only.
            It does not provide financial advice, trading signals, or investment recommendations.
            All trading decisions are solely your responsibility. Past trading performance does not
            guarantee future results.
          </p>
        </div>
      </div>
    </div>
  );
}
